import { useCallback, useEffect, useState } from 'react'
import type { User } from './Login'
import { useI18n, formatar } from '../lib/i18n'

/**
 * Identidade: qual dos três níveis o operador tem, e como subir de nível.
 *
 * O app deixou de exigir indicativo oficial no cadastro, e essa decisão precisa
 * ficar visível em algum lugar — senão quem entra com apelido não descobre nem
 * que existe a Faixa do Cidadão liberada, nem que dá para reservar o próprio
 * indicativo comprovando a licença.
 *
 *   livre     apelido. Faixa do Cidadão e serviços abertos.
 *   simulado  emitido por nós na prova. Mesmas faixas da classe.
 *   oficial   indicativo real, só depois de comprovação documental.
 *
 * O pedido daqui não concede nada: ele abre um protocolo. A comprovação pode
 * ir pelo app (upload aqui embaixo) ou por fora, por e-mail — os dois
 * caminhos continuam abertos. O documento enviado pelo app fica visível só
 * para quem analisa, e some sozinho um tempo depois da decisão (docs/10 §2/§4).
 */

type Kind = 'livre' | 'simulado' | 'oficial'

interface Claim {
  id: string
  callsign: string
  class: string
  status: string
  note: string
  created_at: string
  tem_documento: boolean
}

interface ClaimResponse {
  claim: Claim | null
  como_comprovar?: string
  message?: string
}

interface CreateResponse {
  callsign?: string
  como_comprovar?: string
  aviso?: string
  message?: string
}

export function Identidade({
  user,
  onChanged,
  onExcluida,
}: {
  user: User
  onChanged?: () => void
  /** A própria conta acabou de ser excluída — quem chama cuida do logout local. */
  onExcluida?: () => void
}) {
  const { t } = useI18n()
  const NIVEL: Record<Kind, { rotulo: string; cor: string }> = {
    livre: { rotulo: t.identidade.apelido, cor: 'fraco' },
    simulado: { rotulo: t.identidade.indicativoDaProva, cor: '' },
    oficial: { rotulo: t.identidade.indicativoOficial, cor: 'ok' },
  }
  const [open, setOpen] = useState(false)
  const [claim, setClaim] = useState<Claim | null>(null)
  const [contato, setContato] = useState<string | null>(null)
  const [callsign, setCallsign] = useState('')
  const [classe, setClasse] = useState('C')
  const [instrucao, setInstrucao] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [senha, setSenha] = useState('')
  const [atual, setAtual] = useState('')
  const [senhaMsg, setSenhaMsg] = useState<string | null>(null)
  const [senhaErr, setSenhaErr] = useState<string | null>(null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [enviandoDoc, setEnviandoDoc] = useState(false)
  const [docMsg, setDocMsg] = useState<string | null>(null)
  const [docErr, setDocErr] = useState<string | null>(null)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)
  const [digitadoExclusao, setDigitadoExclusao] = useState('')
  const [excluindoConta, setExcluindoConta] = useState(false)
  const [erroExclusao, setErroExclusao] = useState<string | null>(null)

  const load = useCallback(() => {
    fetch('/api/indicativo/pedido')
      .then((r) => r.json() as Promise<ClaimResponse>)
      .then((d) => {
        setClaim(d.claim ?? null)
        setContato(d.como_comprovar ?? null)
      })
      .catch(() => undefined)
  }, [])

  useEffect(load, [load])

  const nivel = (user.kind ?? 'livre') as Kind
  const info = NIVEL[nivel] ?? NIVEL.livre

  const pedir = async () => {
    setBusy(true)
    setErr(null)
    setInstrucao(null)
    try {
      const r = await fetch('/api/indicativo/pedido', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ callsign, class: classe }),
      })
      const d = (await r.json()) as CreateResponse
      if (!r.ok) {
        setErr(d.message ?? t.identidade.erroAbrirPedido)
        return
      }
      setInstrucao([d.como_comprovar, d.aviso].filter(Boolean).join(' '))
      setCallsign('')
      load()
      onChanged?.()
    } catch {
      setErr(t.identidade.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  // Reenviar substitui o documento anterior — é como se corrige um envio
  // errado, sem precisar abrir outro pedido.
  const enviarDocumento = async () => {
    if (!arquivo) return
    setEnviandoDoc(true)
    setDocErr(null)
    setDocMsg(null)
    try {
      const fd = new FormData()
      fd.append('documento', arquivo)
      const r = await fetch('/api/indicativo/pedido/documento', { method: 'POST', body: fd })
      const d = (await r.json()) as { message?: string }
      if (!r.ok) {
        setDocErr(d.message ?? t.identidade.erroEnviarDocumento)
        return
      }
      setDocMsg(t.identidade.documentoEnviado)
      setArquivo(null)
      load()
    } catch {
      setDocErr(t.identidade.erroServidor)
    } finally {
      setEnviandoDoc(false)
    }
  }

  // Senha: o atalho de quem já entrou uma vez. O link do e-mail continua
  // existindo e é ele que recupera senha esquecida — sem esse caminho de volta,
  // senha perdida seria logbook perdido.
  const gravarSenha = async (remover: boolean) => {
    setBusy(true)
    setSenhaErr(null)
    setSenhaMsg(null)
    try {
      const r = await fetch('/api/auth/senha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ atual, nova: senha, remover }),
      })
      const d = (await r.json()) as { message?: string }
      if (!r.ok) {
        setSenhaErr(d.message ?? t.identidade.erroGravar)
        return
      }
      setSenhaMsg(d.message ?? t.identidade.pronto)
      setSenha('')
      setAtual('')
      onChanged?.()
    } catch {
      setSenhaErr(t.identidade.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  // Exclusão da própria conta: irreversível e em cascata, mesmo raciocínio
  // da mesa de admin — por isso pede o indicativo digitado, não só um clique.
  const excluirConta = async () => {
    setExcluindoConta(true)
    setErroExclusao(null)
    try {
      const r = await fetch('/api/conta', { method: 'DELETE' })
      if (!r.ok) {
        const d = (await r.json().catch(() => ({}))) as { message?: string }
        setErroExclusao(d.message ?? t.identidade.erroExcluirConta)
        return
      }
      onExcluida?.()
    } catch {
      setErroExclusao(t.identidade.erroServidor)
    } finally {
      setExcluindoConta(false)
    }
  }

  return (
    <section className="station">
      <button className="st-toggle" onClick={() => setOpen((v) => !v)}>
        {t.identidade.minhaIdentidade}
        <small>
          {user.callsign} · {info.rotulo}
        </small>
      </button>

      {open && (
        <>
          <p className="aviso">{user.nota_identidade}</p>

          {nivel === 'livre' && (
            <p className="st-hint">
              {t.identidade.hintLivrePre} <b>{t.identidade.hintLivreForte}</b>{' '}
              {t.identidade.hintLivrePos}
            </p>
          )}

          {nivel === 'simulado' && <p className="st-hint">{t.identidade.hintSimulado}</p>}

          {nivel === 'oficial' && <p className="st-hint">{t.identidade.hintOficial}</p>}

          <div className="st-field">
            <label htmlFor="senha-nova">
              {user.tem_senha ? t.identidade.trocarSenha : t.identidade.definirSenha}
            </label>
            {user.tem_senha && (
              <input
                id="senha-atual"
                type="password"
                placeholder={t.identidade.senhaAtualPlaceholder}
                value={atual}
                onChange={(e) => setAtual(e.target.value)}
                autoComplete="current-password"
              />
            )}
            <input
              id="senha-nova"
              type="password"
              placeholder={t.identidade.novaSenhaPlaceholder}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="new-password"
            />
            <small>{t.identidade.senhaHint}</small>
            {senhaErr && <p className="denial">{senhaErr}</p>}
            {senhaMsg && <p className="st-ok">{senhaMsg}</p>}
            <button
              className="st-save"
              disabled={busy || senha.length < 8}
              onClick={() => void gravarSenha(false)}
            >
              {user.tem_senha ? t.identidade.trocarSenhaBotao : t.identidade.definirSenhaBotao}
            </button>
            {user.tem_senha && (
              <button className="link" disabled={busy} onClick={() => void gravarSenha(true)}>
                {t.identidade.removerSenha}
              </button>
            )}
          </div>

          {claim && (
            <>
              <p className="aviso">
                {t.identidade.pedidoEmAnalise} <b>{claim.callsign}</b>
                {claim.class ? ` ${formatar(t.identidade.pedidoClasseTemplate, { classe: claim.class })}` : ''}
                . {t.identidade.pedidoMeio}
                {contato ? ` ${formatar(t.identidade.enviarComprovacaoTemplate, { contato })}` : ''}
              </p>

              <div className="st-field">
                <label htmlFor="doc-upload">{t.identidade.documentoLabel}</label>
                <input
                  id="doc-upload"
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                />
                <small>
                  {claim.tem_documento
                    ? t.identidade.documentoJaEnviado
                    : t.identidade.documentoAindaNao}
                </small>
                {docErr && <p className="denial">{docErr}</p>}
                {docMsg && <p className="st-ok">{docMsg}</p>}
                <button
                  className="st-save"
                  disabled={!arquivo || enviandoDoc}
                  onClick={() => void enviarDocumento()}
                >
                  {enviandoDoc ? t.identidade.enviandoDocumento : t.identidade.enviarDocumentoBotao}
                </button>
              </div>
            </>
          )}

          {!claim && nivel !== 'oficial' && (
            <>
              <div className="st-field">
                <label htmlFor="ind-oficial">{t.identidade.indicativoOficialSeu}</label>
                <input
                  id="ind-oficial"
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                  placeholder={t.identidade.callsignPlaceholder}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <small>{t.identidade.reservarHint}</small>
              </div>

              <div className="st-field">
                <label htmlFor="ind-classe">{t.identidade.classeAtiva}</label>
                <select
                  id="ind-classe"
                  value={classe}
                  onChange={(e) => setClasse(e.target.value)}
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>

              {err && <p className="denial">{err}</p>}
              {instrucao && <p className="st-ok">{instrucao}</p>}

              <button
                className="st-save"
                onClick={() => void pedir()}
                disabled={busy || callsign.length < 3}
              >
                {busy ? t.identidade.abrindo : t.identidade.pedirReserva}
              </button>
              <p className="st-hint">{t.identidade.naoGuardamos}</p>
            </>
          )}

          <div className="st-field">
            <button className="link" onClick={() => setConfirmandoExclusao((v) => !v)}>
              {t.identidade.excluirContaToggle}
            </button>
            {confirmandoExclusao && (
              <>
                <p className="aviso">{t.identidade.excluirContaAviso}</p>
                <label htmlFor="excluir-confirma">
                  {t.identidade.digiteSeuIndicativo} <b>{user.callsign}</b>{' '}
                  {t.identidade.paraExcluirConta}
                </label>
                <input
                  id="excluir-confirma"
                  value={digitadoExclusao}
                  onChange={(e) => setDigitadoExclusao(e.target.value.toUpperCase())}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {erroExclusao && <p className="denial">{erroExclusao}</p>}
                <button
                  className="st-save adm-perigo"
                  disabled={excluindoConta || digitadoExclusao !== user.callsign}
                  onClick={() => void excluirConta()}
                >
                  {excluindoConta ? t.identidade.excluindoConta : t.identidade.excluirContaBotao}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </section>
  )
}
